import NoCharts from "@/assets/images/no-charts.svg";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Flex,
  Image,
  Skeleton,
  Spin,
  theme,
  Typography,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ErrorScreen } from "../components/ErrorScreen";
import { DeleteChartModal } from "../components/modals/DeleteChartModal";
import { DuplicateChartModal } from "../components/modals/DuplicateChartModal";
import { useToast } from "../contexts/toast/useToast";
import { Chart, useChartQuery } from "../graphql/query.charts";
import { formatDateTime } from "../helpers/date";
import { CHART, NEW_CHART } from "../routes";
import { useGenChartsScreenshotsMutation } from "../graphql/mutation.genChartScreenshot";
import { useStore } from "../store/store";
import { useShallow } from "zustand/shallow";
import { MaxAllowedFreeCharts } from "../constants";
import SubscriptionPopover from "../components/popovers/SubscriptionPopover";
import { useAuthContext } from "../contexts/auth/useAuthContext";

const HomePage = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasSubscription } = useAuthContext();
  const { open } = useToast();
  const [charts, setCharts] = useState<Chart[]>([]);
  // {chartId: screenshotUrl}
  const [chartsImages, setChartsImages] = useState<Record<string, string>>({});
  const { incrementChartsCount, decrementChartsCount, chartsCount } = useStore(
    useShallow((state) => ({
      incrementChartsCount: state.incrementChartsCount,
      decrementChartsCount: state.decrementChartsCount,
      chartsCount: state.chartsCount,
    }))
  );

  const {
    data: getChartsQueryResponse,
    error: getChartsError,
    loading: isChartsLoading,
  } = useChartQuery();

  const [selectedChart, setSelectedChart] = useState<Chart | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [
    runGenChartsScreenshots,
    {
      loading: isGenChartsScreenshotsLoading,
      error: isGenChartsScreenshotsError,
    },
  ] = useGenChartsScreenshotsMutation();

  const runGenChartsScreenshotsMutation = useCallback(
    async (chartsIDs: string[]) => {
      const response = await runGenChartsScreenshots({
        variables: {
          input: {
            chartsIDs,
          },
        },
      });

      if (response.data) {
        const screenshots = response.data.genChartsScreenshots.screenshots;
        const newChartsImages = screenshots.reduce(
          (acc, { chartID, screenshotUrl }) => {
            acc[chartID] = screenshotUrl;
            return acc;
          },
          {} as Record<string, string>
        );
        setChartsImages((prev) => ({ ...prev, ...newChartsImages }));
      }
    },
    [runGenChartsScreenshots]
  );

  const loadChartsScreenshots = useCallback(
    async (chartsIDs: string[]) => {
      let attempts = 0;
      const maxAttempts = 4; // Maximum number of attempts to generate screenshots
      // Retry logic to ensure the screenshot generation is successful
      while (attempts < maxAttempts) {
        try {
          await runGenChartsScreenshotsMutation(chartsIDs);
          break;
        } catch (error) {
          // wait for a short period before retrying, with backoff
          await new Promise((resolve) =>
            setTimeout(resolve, 2000 * (attempts + 1))
          );
          attempts++;
        }
        if (attempts >= maxAttempts) {
          console.error("Max attempts reached. Failed to generate screenshot.");
          break;
        }
      }
    },
    [runGenChartsScreenshotsMutation]
  );

  useEffect(() => {
    if (getChartsQueryResponse && getChartsQueryResponse.charts) {
      setCharts(getChartsQueryResponse.charts.charts);
      //setChartsCount(getChartsQueryResponse.charts.totalCount);
      const chartsWithScreenshots: Record<string, string> = {};
      const chartsWithoutScreenshots =
        getChartsQueryResponse.charts.charts.filter((chart) => {
          const hasScreenshot = !!chart.screenshotUrl;
          if (hasScreenshot) {
            chartsWithScreenshots[chart.id] = chart.screenshotUrl!;
          }
          return !hasScreenshot;
        });
      setChartsImages((prev) => ({ ...prev, ...chartsWithScreenshots }));
      if (chartsWithoutScreenshots.length > 0) {
        void loadChartsScreenshots(
          chartsWithoutScreenshots.map((chart) => chart.id)
        );
      }
    }
  }, [getChartsQueryResponse, loadChartsScreenshots]);

  const handleDuplicateChartSuccess = (newChart: Chart) => {
    setCharts((prevCharts) => [newChart, ...prevCharts]);
    void loadChartsScreenshots([newChart.id]);
    incrementChartsCount();
    open("success", t("chart_duplicated"));
    hideModalDuplicateChart();
  };

  const handleDeleteChartSuccess = (deletedChartId: string) => {
    setCharts((prevCharts) =>
      prevCharts.filter((chart) => chart.id !== deletedChartId)
    );
    open("success", t("chart_deleted"));
    decrementChartsCount();
    hideConfirmDeleteChart();
  };

  if (getChartsError) {
    return <ErrorScreen />;
  }

  const showConfirmDeleteChart = (chart: Chart) => {
    setSelectedChart(chart);
    setIsDeleteConfirmOpen(true);
  };

  const hideConfirmDeleteChart = () => {
    setIsDeleteConfirmOpen(false);
    setSelectedChart(null);
  };

  const showModalDuplicateChart = async (chart: Chart) => {
    setSelectedChart(chart);
    setIsDuplicateModalOpen(true);
  };

  const hideModalDuplicateChart = () => {
    setIsDuplicateModalOpen(false);
    setSelectedChart(null);
  };

  const goToChart = (chartId: string) => () => {
    navigate(CHART.replace(":chartId", chartId));
  };

  // #region JSX
  return (
    <>
      <Flex
        vertical
        gap={token.marginLG}
        style={{
          height: "100%",
          maxHeight: "calc(100vh - 130px)",
          overflow: "auto",
        }}
      >
        {isChartsLoading && (
          <Flex
            justify="center"
            flex={1}
            align="center"
            style={{
              width: "100%",
            }}
          >
            <Spin size="large" />
          </Flex>
        )}
        {!isChartsLoading && getChartsQueryResponse && (
          <Flex
            wrap="wrap"
            flex={1}
            gap={token.marginMD}
            style={{
              position: "relative",
              alignContent: "flex-start",
            }}
          >
            {charts.length === 0 && (
              <Flex
                justify="center"
                align="center"
                vertical
                gap={token.marginLG}
                style={{
                  width: "100%",
                  height: "100%",
                }}
              >
                <img src={NoCharts} title={t("no_charts")} width={256} />
                <Typography.Text type="secondary">
                  {t("no_charts")}
                </Typography.Text>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate(NEW_CHART)}
                >
                  {t("new_chart")}
                </Button>
              </Flex>
            )}
            {charts.map((chart) => (
              <Card key={chart.id} style={{ width: "100%" }}>
                <Flex vertical gap={token.marginSM}>
                  <Flex align="center" gap={token.marginSM}>
                    {!chartsImages[chart.id] &&
                      (isGenChartsScreenshotsLoading ||
                        isGenChartsScreenshotsError) && (
                        <Skeleton.Image
                          active={!isGenChartsScreenshotsError}
                          style={{
                            width: 128,
                            height: 128,
                            borderRadius: token.borderRadiusLG,
                            overflow: "hidden",
                          }}
                        />
                      )}
                    {chartsImages[chart.id] && (
                      <div
                        style={{
                          width: 130,
                          height: 128,
                          placeContent: "center",
                          backgroundColor: token.colorBgTextActive,
                          border: `1px solid ${token.colorBorder}`,
                          borderRadius: token.borderRadiusSM,
                          textAlign: "center",
                          cursor: "pointer",
                        }}
                        onClick={goToChart(chart.id)}
                      >
                        <Image
                          preview={false}
                          style={{ width: 128, maxHeight: 128, flex: 1 }}
                          src={`${chartsImages[chart.id]}?d=${Date.now()}`}
                        />
                      </div>
                    )}
                    <Flex vertical style={{ width: "100%" }}>
                      <Flex justify="flex-end" gap={token.marginXS}>
                        <Button
                          type="text"
                          size="small"
                          icon={
                            <DeleteOutlined
                              style={{ color: token.colorError }}
                            />
                          }
                          iconPosition="start"
                          loading={
                            isDeleteConfirmOpen &&
                            chart.id === selectedChart?.id
                          }
                          onClick={() => {
                            showConfirmDeleteChart(chart);
                          }}
                        >
                          {t("delete")}
                        </Button>
                        {!hasSubscription &&
                          chartsCount >= MaxAllowedFreeCharts && (
                            <SubscriptionPopover
                              chartsCount={chartsCount}
                              placement="bottom"
                            >
                              <Button
                                type="text"
                                size="small"
                                icon={<CopyOutlined />}
                                iconPosition="start"
                                disabled={true}
                                loading={
                                  isDuplicateModalOpen &&
                                  chart.id === selectedChart?.id
                                }
                                onClick={() => {
                                  void showModalDuplicateChart(chart);
                                }}
                              >
                                {t("duplicate")}
                              </Button>
                            </SubscriptionPopover>
                          )}
                        {(hasSubscription ||
                          chartsCount < MaxAllowedFreeCharts) && (
                          <Button
                            type="text"
                            size="small"
                            icon={<CopyOutlined />}
                            iconPosition="start"
                            loading={
                              isDuplicateModalOpen &&
                              chart.id === selectedChart?.id
                            }
                            onClick={() => {
                              void showModalDuplicateChart(chart);
                            }}
                          >
                            {t("duplicate")}
                          </Button>
                        )}
                        <Button
                          type="text"
                          size="small"
                          icon={
                            <EditOutlined
                              style={{ color: token.colorPrimary }}
                            />
                          }
                          iconPosition="start"
                          onClick={goToChart(chart.id)}
                        >
                          {t("edit")}
                        </Button>
                      </Flex>
                      <Typography.Text
                        strong
                        onClick={goToChart(chart.id)}
                        title={t("edit_this_chart")}
                        style={{
                          cursor: "pointer",
                          fontSize: token.fontSizeLG,
                        }}
                      >
                        {chart.title}
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        {t("creation_date", {
                          createdAt: formatDateTime(chart.createdAt),
                        })}
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        {t("last_edition", {
                          updatedAt: formatDateTime(chart.updatedAt),
                        })}
                      </Typography.Text>
                    </Flex>
                  </Flex>
                </Flex>
              </Card>
            ))}
          </Flex>
        )}
      </Flex>
      {isDeleteConfirmOpen && selectedChart && (
        <DeleteChartModal
          chart={selectedChart}
          onSuccess={handleDeleteChartSuccess}
          onError={(error) => {
            console.error(error);
            open("error", t("chart_deletion_failed"));
            hideConfirmDeleteChart();
          }}
          onCancel={() => {
            hideConfirmDeleteChart();
            setSelectedChart(null);
          }}
        />
      )}
      {isDuplicateModalOpen && selectedChart && (
        <DuplicateChartModal
          chart={selectedChart}
          onSuccess={handleDuplicateChartSuccess}
          onError={(error) => {
            console.error(error);
            open("error", t("chart_duplicated_failed"));
            hideModalDuplicateChart();
          }}
          onCancel={() => {
            hideModalDuplicateChart();
          }}
        />
      )}
    </>
  );
};

export default HomePage;
